import {loggerFactory} from "@cm/browser-log"
import {AnyImageOpContext, ImageOpNode, ImageOpOutputs} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/node/image-op-node"
import {imageOpNode} from "@app/textures/texture-editor/operator-stack/image-op-system/helpers/image-op-node-helpers"
import {EvaluatedObject} from "@app/textures/texture-editor/operator-stack/image-op-system/image-op-nodes/utils/lambda/helpers"
import {GraphNode, NodeParameters} from "@cm/graph-v3"
import {ImageOpNodeLambda} from "@app/textures/texture-editor/operator-stack/image-op-system/image-op-nodes/utils/lambda/node"
import {ImageOpNodeGetProperty} from "@app/textures/texture-editor/operator-stack/image-op-system/image-op-nodes/utils/get-property/node"
import {REF_COUNT_DEBUG_ID_INITIAL} from "@cm/utils"

export class ImageOpNodeBag {
    constructor(public debugId: string | undefined) {}

    dispose() {
        if (this._inFrame) {
            throw new Error("ImageOpNodeBag.dispose() called while still in a frame. Call endFrame() before disposing.")
        }
        this._disposed = true
        this.nodeById.forEach((node) => node.release(REF_COUNT_DEBUG_ID_INITIAL))
        this.nodeById.clear()
        this.idByNode.clear()
    }

    beginFrame() {
        if (this._disposed) {
            throw new Error("Attempting to use disposed ImageOpNodeBag")
        }
        if (this._inFrame) {
            throw new Error("ImageOpNodeBag.beginFrame() called while already in a frame")
        }
        this._inFrame = true
        this.usedNodes.clear()
    }

    endFrame() {
        if (this._disposed) {
            throw new Error("Attempting to use disposed ImageOpNodeBag")
        }
        if (!this._inFrame) {
            throw new Error("ImageOpNodeBag.endFrame() called without a matching beginFrame()")
        }
        this._inFrame = false
        this.removeUnusedNodes()
    }

    async runInFrame<T>(fn: () => Promise<T>): Promise<T> {
        this.beginFrame()
        try {
            return await fn()
        } finally {
            this.endFrame()
        }
    }

    runInFrameSync<T>(fn: () => T): T {
        this.beginFrame()
        try {
            return fn()
        } finally {
            this.endFrame()
        }
    }

    removeNode(id: string) {
        if (this._disposed) {
            throw new Error("Attempting to use disposed ImageOpNodeBag")
        }
        const node = this.nodeById.get(id)
        if (!node) {
            throw new Error(`ImageOpNodeBag.removeNode() called for node ${id} that is not in the bag`)
        }
        this.usedNodes.delete(node)
        this.nodeById.delete(id)
        this.idByNode.delete(node)
        node.release(REF_COUNT_DEBUG_ID_INITIAL)
        ImageOpNodeBag.logger.trace(`ImageOpNodeBag: removed node ${id} (${node.debugId})`)
    }

    removeNodeByInstance(node: ImageOpNode) {
        if (this._disposed) {
            throw new Error("Attempting to use disposed ImageOpNodeBag")
        }
        const id = this.idByNode.get(node)
        if (!id) {
            throw new Error(`ImageOpNodeBag.removeNodeByInstance() called for node that is not in the bag`)
        }
        this.removeNode(id)
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    getNode<Ctor extends new (...args: any[]) => ImageOpNode>(
        id: string,
        ctor: Ctor,
        parameters: ConstructorParameters<Ctor>[0],
        postCtor?: (instance: InstanceType<Ctor>) => void,
    ): InstanceType<Ctor> {
        if (this._disposed) {
            throw new Error("Attempting to use disposed ImageOpNodeBag")
        }
        if (!this._inFrame) {
            throw new Error("ImageOpNodeBag.getNode() called outside of a frame")
        }
        let node = this.getExistingNode<Ctor>(id)
        if (node && this.usedNodes.has(node)) {
            throw new Error(`ImageOpNodeBag.getExistingNode() called multiple times within a frame for node ${id}`)
        }
        node = imageOpNode(node, ctor, parameters, postCtor)
        node.debugId = this.debugId ? `${this.debugId}.${id}` : id
        this.nodeById.set(id, node)
        this.idByNode.set(node, id)
        this.usedNodes.add(node)
        return node
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    getNodeProperty<Ctor extends new (...args: any[]) => ImageOpNode, Node extends ImageOpNode, Key extends string & keyof ImageOpOutputs<Node["definition"]>>(
        id: string,
        ctor: Ctor,
        parameters: ConstructorParameters<Ctor>[0],
        property: string & keyof ImageOpOutputs<InstanceType<Ctor>["definition"]>,
        postCtor?: (instance: InstanceType<Ctor>) => void,
    ): GraphNode<ImageOpOutputs<Node["definition"]>[Key], AnyImageOpContext> {
        const node = this.getNode(id, ctor, parameters, postCtor)
        return this.getProperty(id + `[${property}]`, node, property)
    }

    getLambda<Args extends NodeParameters, Return>(id: string, args: Args, fn: (args: EvaluatedObject<Args>) => Return) {
        return this.getNode(id, ImageOpNodeLambda, {
            args,
            fn,
        })
    }

    getProperty<Node extends ImageOpNode, Return, Key extends string & keyof ImageOpOutputs<Node["definition"]>>(id: string, value: Node, key: Key) {
        return this.getNode(id, ImageOpNodeGetProperty<Return>, {
            value,
            key,
        })
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private getExistingNode<Ctor extends new (...args: any[]) => ImageOpNode>(id: string): InstanceType<Ctor> | undefined {
        if (this._disposed) {
            throw new Error("Attempting to use disposed ImageOpNodeBag")
        }
        return this.nodeById.get(id) as InstanceType<Ctor> | undefined
    }

    private removeUnusedNodes() {
        this.nodeById.forEach((node, id) => {
            if (!this.usedNodes.has(node)) {
                this.removeNode(id)
            }
        })
        this.usedNodes.clear()
    }

    private nodeById = new Map<string, ImageOpNode>()
    private idByNode = new Map<ImageOpNode, string>()
    private usedNodes = new Set<ImageOpNode>()
    private _inFrame = false
    private _disposed = false // for debugging purposes
    // TODO: remove static logger, replace with instance logger
    private static readonly logger = loggerFactory.getLogger("image-op-system")
}
